import {
  computeChapterChangeStatuses,
  computeLessonChangeStatuses,
  reconcileCourse,
  type Course,
} from "@repo/app-types/entities/course";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import type {
  ProjectWithInterviewCount,
  ProjectWithJobType,
} from "@repo/app-types/entities/project";
import { useCallback, useMemo, useState } from "react";
import { useAllInterviewSectionDefinitionsForJobTypeQuery } from "../../../../api/entities/interviewSectionDefinition.js";
import { useUpdateJobTypeMutation } from "../../../../api/entities/jobType.js";
import { useAppDispatch } from "../../../../hooks/hook.js";
import { applyInterviewSectionDefinitionChanges } from "../../../interviewSections/definitions/state.js";
import { useInterviewSectionDefinitionsStore } from "../../../interviewSections/definitions/store.js";
import { fetchProjectById } from "../../projectsSlice.js";
import { mapSectionClientIdsToServerIds } from "./courseHelpers.js";

export function useLearningJourneyEditor(
  project: ProjectWithInterviewCount & ProjectWithJobType,
) {
  const jobTypeId = project.job_type.id;
  const dispatch = useAppDispatch();
  const [updateJobType, { isLoading: isSaving }] = useUpdateJobTypeMutation();
  const { data: fetchedSections, isLoading: isLoadingSections } =
    useAllInterviewSectionDefinitionsForJobTypeQuery({
      jobTypeId,
    });

  const {
    changedSections,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onSectionsSave,
    onSectionsReset,
    hasMadeChanges: hasMadeChangesToSections,
  } = useInterviewSectionDefinitionsStore(jobTypeId);

  const sections = useMemo(
    () =>
      applyInterviewSectionDefinitionChanges(fetchedSections, changedSections),
    [fetchedSections, changedSections],
  );

  const sectionsById = useMemo(
    () =>
      sections
        ? new Map(sections.map((section) => [section.id, section] as const))
        : undefined,
    [sections],
  );

  const persistedCourse = useMemo(() => {
    if (!fetchedSections) {
      return project.job_type.settings.course ?? { chapters: [] };
    }

    return reconcileCourse(project.job_type.settings.course, fetchedSections);
  }, [project.job_type.settings.course, fetchedSections]);

  const [changedCourse, setChangedCourse] = useState<Course | undefined>(
    undefined,
  );
  const [changedPlaybookId, setChangedPlaybookId] = useState<
    PlaybookId | null | undefined
  >(undefined);

  const playbookId =
    changedPlaybookId === undefined
      ? project.job_type.settings.playbook_id
      : changedPlaybookId;

  const course = useMemo(() => {
    if (!sections) {
      return changedCourse ?? persistedCourse;
    }

    return reconcileCourse(changedCourse ?? persistedCourse, sections);
  }, [changedCourse, persistedCourse, sections]);

  const onCourseChange = useCallback(
    (nextCourse: Course) => {
      if (JSON.stringify(nextCourse) === JSON.stringify(persistedCourse)) {
        setChangedCourse(undefined);
        return;
      }

      setChangedCourse(nextCourse);
    },
    [persistedCourse],
  );

  const onPlaybookChange = useCallback(
    (nextPlaybookId: PlaybookId | null) => {
      if (nextPlaybookId === project.job_type.settings.playbook_id) {
        setChangedPlaybookId(undefined);
        return;
      }

      setChangedPlaybookId(nextPlaybookId);
    },
    [project.job_type.settings.playbook_id],
  );

  const editedLessonIds = useMemo(
    () =>
      changedSections
        .filter((section) => section.changeType === "updated")
        .map((section) => section.id),
    [changedSections],
  );

  const chapterChangeStatuses = useMemo(
    () =>
      computeChapterChangeStatuses(persistedCourse, course, {
        editedLessonIds,
      }),
    [persistedCourse, course, editedLessonIds],
  );

  const lessonChangeStatuses = useMemo(
    () =>
      computeLessonChangeStatuses(persistedCourse, course, {
        editedLessonIds,
      }),
    [persistedCourse, course, editedLessonIds],
  );

  const hasCourseChanges =
    JSON.stringify(course) !== JSON.stringify(persistedCourse);
  const hasPlaybookChanges =
    playbookId !== project.job_type.settings.playbook_id;
  const hasMadeChanges =
    hasCourseChanges || hasPlaybookChanges || hasMadeChangesToSections;

  const onDiscardChanges = useCallback(() => {
    setChangedCourse(undefined);
    setChangedPlaybookId(undefined);
    onSectionsReset();
  }, [onSectionsReset]);

  const onSaveChanges = useCallback(async () => {
    const savedSections = await onSectionsSave();
    await updateJobType({
      id: jobTypeId,
      settings: {
        ...project.job_type.settings,
        playbook_id: playbookId,
        course: mapSectionClientIdsToServerIds(course, savedSections),
      },
    }).unwrap();

    onSectionsReset();
    setChangedCourse(undefined);
    setChangedPlaybookId(undefined);
    await dispatch(fetchProjectById(project.id)).unwrap();
  }, [
    onSectionsSave,
    updateJobType,
    jobTypeId,
    project.job_type.settings,
    project.id,
    playbookId,
    course,
    onSectionsReset,
    dispatch,
  ]);

  return {
    course,
    sections,
    sectionsById,
    playbookId,
    changedSections,
    chapterChangeStatuses,
    lessonChangeStatuses,
    hasMadeChanges,
    isLoadingSections,
    isSaving,
    onCourseChange,
    onPlaybookChange,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onDiscardChanges,
    onSaveChanges,
  };
}
